import {FC} from 'react'
import {PrintFlowOutcome} from '../printFlow/outcomeModel'

const derivedLabel = (value: string) => {
  switch (value) {
    case 'matched':
      return {text: 'Matched expectation', className: 'badge-light-success'}
    case 'incomplete':
      return {text: 'Incomplete', className: 'badge-light-warning'}
    case 'failed':
      return {text: 'Failed', className: 'badge-light-danger'}
    default:
      return {text: 'Not applicable', className: 'badge-light'}
  }
}

type Props = {outcome: PrintFlowOutcome}

export const ExpectedObservedSummary: FC<Props> = ({outcome}) => {
  const rows = [
    {
      label: 'Printable renders',
      expected: outcome.expected.printableRenders,
      observed: outcome.observed.printableRendersCompleted,
      derived: outcome.derived.rendering,
    },
    {
      label: 'Prefetch devices',
      expected: `${outcome.prefetch.uniqueTargetDevices} devices`,
      observed: `${outcome.prefetch.confirmedDevices} confirmed · ${outcome.prefetch.deliveryAttempts} attempts`,
      derived: outcome.derived.prefetchConfirmation,
    },
    {
      label: 'Physical print jobs',
      expected: `${outcome.stats.printAttempts} attempts`,
      observed: `${outcome.stats.printJobs} successful`,
      derived: outcome.derived.physicalPrinting,
    },
    {
      label: 'Copies',
      expected:
        outcome.stats.autoRequestedCopies > 0
          ? `AUTO ${outcome.stats.autoRequestedCopies} requested`
          : 'No AUTO request',
      observed: `${outcome.stats.confirmedCopiesTotal} confirmed`,
      derived: outcome.derived.physicalPrinting,
    },
  ]

  return (
    <div className='card card-flush border border-gray-200 mb-6'>
      <div className='card-header border-0 pt-5 pb-0'>
        <div className='card-title flex-column align-items-start'>
          <span className='fw-bold text-gray-800'>Expected vs observed</span>
          <span className='text-muted fs-7 fw-semibold mt-1'>
            AUTO requested is original intent. Confirmed copies can be higher after staff or scan prints.
          </span>
        </div>
      </div>
      <div className='card-body pt-5'>
        <div className='table-responsive'>
          <table className='table align-middle gs-0 gy-3 mb-0'>
            <thead>
              <tr className='text-muted fw-bold fs-8 text-uppercase'>
                <th>Signal</th>
                <th>Expected</th>
                <th>Observed</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const derived = derivedLabel(row.derived)
                return (
                  <tr key={row.label}>
                    <td className='fw-semibold text-gray-800'>{row.label}</td>
                    <td className='text-gray-700 fs-6'>{row.expected}</td>
                    <td className='text-gray-700 fs-6'>{row.observed}</td>
                    <td>
                      <span className={`badge ${derived.className}`}>{derived.text}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
